import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { LayoutDashboard, FilePlus2, BookOpen, FolderKanban, LogOut, Plus, Tag, ShieldCheck, GitCompareArrows, Contact } from "lucide-react";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/simulation/new", label: "Nouvelle simulation", icon: FilePlus2 },
  { to: "/catalogue", label: "Catalogue fiches", icon: BookOpen },
  { to: "/projects", label: "Mes projets", icon: FolderKanban },
  { to: "/comparateur", label: "Comparateur", icon: GitCompareArrows },
  { to: "/crm", label: "CRM", icon: Contact, adminOnly: true },
  { to: "/prix", label: "Barème de prix", icon: Tag, hidePartner: true },
  { to: "/admin", label: "Administration", icon: ShieldCheck, adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const nav = NAV.filter((n) => (!n.adminOnly || isAdmin) && (!n.hidePartner || !user?.partner));
  const initials = (user?.name || "U")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <button onClick={() => navigate("/")} className="shrink-0" data-testid="nav-logo-btn">
            <BrandLogo partner={user?.partner} />
          </button>

          <nav className="hidden lg:flex items-center gap-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-link-${n.to === "/" ? "dashboard" : n.to.replace(/\//g, "-").slice(1)}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-genis/10 text-genis-dark dark:text-genis"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`
                }
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button
              size="sm"
              className="bg-genis hover:bg-genis-dark text-white lg:hidden"
              onClick={() => navigate("/simulation/new")}
              data-testid="nav-new-mobile-btn"
            >
              <Plus className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="outline-none">
                  <Avatar className="h-9 w-9 border-2 border-genis/30">
                    <AvatarFallback className="bg-genis text-white font-semibold text-sm">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                  <div className="text-[11px] font-semibold text-genis mt-1" data-testid="user-space-label">
                    {isAdmin ? "Administrateur" : user?.partner ? `Espace ${user.partner.name}` : "Espace Genis Energie"}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/projects")} data-testid="menu-projects">
                  <FolderKanban className="h-4 w-4 mr-2" /> Mes projets
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <ShieldCheck className="h-4 w-4 mr-2" /> Administration
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <main>
        <Outlet />
      </main>
    </div>
  );
}
